import { LimitOrderSubmitStatus } from './../../../state/dex/type'
import {
  updateLimitOrderSubmitStatus,
  updateLimitSwapPairAmount,
} from '~/state/dex/actions'
import { LimitOrderRequest } from '@tokenlon/v6-sdk'
import { useCallback } from 'react'
import { AddressZero } from '@ethersproject/constants'
import { useGA } from '~/hooks'
import {
  useLimitOrderUserConfig,
  useLimitSwapAmount,
  useLimitSwapPair,
} from '~/state/dex/hooks'
import { parseUnits } from 'ethers/lib/utils'
import { useAppDispatch } from '~/state/hooks'
import {
  ApplicationModal,
  updateRefreshToken,
} from '~/state/application/reducer'
import { useModalSwitch } from '~/state/application/hooks'
import { initialState } from '~/state/dex/reducer'
import { captureException } from '@sentry/nextjs'
import dayjs from 'dayjs'
import { useAccount } from 'wagmi'
import { useV6SDK } from '~/hooks'
import { Address } from 'viem'
import {
  isUserRejectedError,
  TRACK_USER_REJECTED_MSG,
} from '~/utils/handleUserRejectedError'

export const useTradeSubmit = () => {
  const { address: account, connector } = useAccount()
  const sdk = useV6SDK()
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const { expiration } = useLimitOrderUserConfig()
  const switchReview = useModalSwitch(ApplicationModal.LIMIT_ORDER_PREVIEW)
  const dispatch = useAppDispatch()
  const { trackClick } = useGA()

  return useCallback(async () => {
    dispatch(
      updateLimitOrderSubmitStatus(LimitOrderSubmitStatus.SignOrderPending),
    )
    try {
      if (!account) {
        throw new Error('wallet_not_connected')
      }
      const [value, unit] = expiration.split('_')
      const expiry = dayjs()
        .add(Number(value), unit as any)
        .unix()
      const limitOrderRequest: LimitOrderRequest = {
        maker: account as Address,
        taker: AddressZero,
        makerToken: inputToken.address as Address,
        takerToken: outputToken.address as Address,
        makerTokenAmount: parseUnits(
          inputAmount,
          inputToken.decimals,
        ).toString(),
        takerTokenAmount: parseUnits(
          outputAmount,
          outputToken.decimals,
        ).toString(),
        expiry,
      }
      const signedLimitOrder = await sdk.limitOrder.signLimitOrder(
        limitOrderRequest,
      )
      await sdk.limitOrder.createLimitOrder(signedLimitOrder)
      dispatch(updateRefreshToken(+new Date()))
      switchReview(false)
      dispatch(
        updateLimitOrderSubmitStatus(LimitOrderSubmitStatus.SignOrderSuccess),
      )

      dispatch(
        updateLimitSwapPairAmount({
          inputAmount: initialState.limitSwapInputAmount,
          outputAmount: initialState.limitSwapOutputAmount,
        }),
      )

      trackClick('webtl_lo_preview_confirm', {
        taker_token_symbol: outputToken.symbol,
        maker_token_symbol: inputToken.symbol,
        taker_token_amount: outputAmount,
        maker_token_amount: inputAmount,
        type: expiration,
        is_success: 1,
      })
    } catch (error) {
      console.error(error)

      const isUserRejected = isUserRejectedError(error)

      if (!isUserRejected) {
        captureException(error)
      }

      trackClick('webtl_lo_preview_confirm', {
        taker_token_symbol: outputToken.symbol,
        maker_token_symbol: inputToken.symbol,
        taker_token_amount: outputAmount,
        maker_token_amount: inputAmount,
        type: expiration,
        is_success: 0,
        message: isUserRejected ? TRACK_USER_REJECTED_MSG : error?.message,
      })

      if (error?.isAxiosError && error?.response?.data.code) {
        dispatch(
          updateLimitOrderSubmitStatus(
            LimitOrderSubmitStatus[
              `SignOrderFailureCode${error.response.data.code}`
            ],
          ),
        )
      } else {
        dispatch(
          updateLimitOrderSubmitStatus(LimitOrderSubmitStatus.SignOrderFailure),
        )
      }
    }
  }, [
    account,
    connector,
    expiration,
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
  ])
}
